import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { inr } from "../lib/api";

export const ProductCard = ({ p, index = 0 }) => {
  const { add, setOpen } = useCart();
  const [hover, setHover] = useState(false);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 200, damping: 20 });

  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const quickAdd = (e) => {
    e.preventDefault();
    const size = p.sizes.includes("L") ? "L" : p.sizes[0];
    add(p, size);
    toast.success(`${p.name} · Size ${size} added`);
    setOpen(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.9, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      style={{ perspective: 1000 }}
    >
      <Link to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group block">
        <motion.div
          onMouseMove={move}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => { setHover(false); mx.set(0); my.set(0); }}
          style={{ rotateX: rx, rotateY: ry }}
          className="clip-notch relative aspect-[3/4] overflow-hidden bg-[#18181C]"
        >
          <img src={p.image} alt={p.name} className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-700 group-hover:scale-105 ${hover ? "opacity-0" : "opacity-100"}`} />
          <img src={p.secondary_image} alt="" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${hover ? "opacity-100" : "opacity-0"}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <span className="absolute left-4 top-4 bg-[#00FF66] px-2 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-black">{p.badge}</span>
          <span className="absolute right-8 top-4 font-mono text-xs text-white/80">/{String(index + 1).padStart(2, "0")}</span>
          <button
            data-testid={`quick-add-btn-${p.slug}`}
            onClick={quickAdd}
            className="absolute bottom-4 right-4 flex items-center gap-1 bg-white px-3 py-2 font-mono text-xs font-bold uppercase text-black transition-[transform,background-color] duration-300 hover:bg-[#00FF66] md:translate-y-16 md:group-hover:translate-y-0"
          >
            <Plus className="h-3.5 w-3.5" /> Quick add
          </button>
        </motion.div>
        <div className="mt-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-3xl font-black uppercase leading-none transition-colors group-hover:text-[#00FF66]">{p.name}</h3>
            <p className="mt-1 text-sm text-zinc-400">{p.tagline}</p>
          </div>
          <p className="font-mono text-lg font-bold" data-testid={`product-price-${p.slug}`}>{inr(p.price)}</p>
        </div>
      </Link>
    </motion.div>
  );
};
